import { type IReactionDisposer, makeAutoObservable } from 'mobx'
import { type CancelTokenSource, AxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import {
  ComplianceApi,
  type ICountriesEnum,
  type IInternationalBusinessInformation,
  type IResponseInternationalComplianceRegulationsItem,
} from 'entities/Compliance'
import type { IResponseCountry } from 'entities/Auth'
import { AuthApi } from 'entities/Auth/api'
import type { IResponseEinItem } from 'entities/Compliance/api/types/complianceType'
import { ContactInfoStore } from 'pages/settings/pages/compliance/pages/international/ui/ContactInfo/store/contactInfoStore'
import type { ComplianceLayerStore } from 'pages/settings/pages/compliance/store/complianceLayerStore'
import { StepsStore } from 'pages/settings/store/stepsStore'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { InternationalRoutes } from 'pages/settings/pages/compliance/pages/international'
import { BusinessInfoStore } from 'pages/settings/pages/compliance/pages/international/ui/BusinessInfo/store/BusinessInfoStore'
import { BusinessAddressFormStore, type IAddressData } from 'pages/settings/ui/BusinessAddressForm'

const titleMap: Record<string, string> = {
  [InternationalRoutes.businessInfo]: 'Business info',
  [InternationalRoutes.businessAddress]: 'Business address',
  [InternationalRoutes.contactInfo]: 'Authorized representative',
  [InternationalRoutes.finalDetails]: 'Final details',
}

export class InternationalStepsStore {
  private _cancelPageSource: CancelTokenSource | null = null
  private _disposePageReaction: IReactionDisposer | null = null

  private _isTermsAndPrivacyChecked = false

  private _allCountries: IResponseCountry[] = []
  private _einItem: IInternationalBusinessInformation | null = null

  private _stepsStore = new StepsStore()

  private readonly _businessInfoStore: BusinessInfoStore
  private readonly _businessAddressFormStore: BusinessAddressFormStore
  private readonly _contactInfoStore: ContactInfoStore

  private _regulations: IResponseInternationalComplianceRegulationsItem[] | null = null
  private _bundleId: number | null = null

  constructor(private _complianceLayerStore: ComplianceLayerStore) {
    this._businessInfoStore = new BusinessInfoStore(this)
    this._businessAddressFormStore = new BusinessAddressFormStore({
      onFieldChange: (field, value) => this.syncBusinessAddressFieldToEinItem(field, value),
      deleteErrorValue: (key) => this.deleteErrorValue(key),
    })
    this._contactInfoStore = new ContactInfoStore(this)

    makeAutoObservable(this)

    this.init()
  }

  get internationalRegulatoryBundlesStore() {
    return this._complianceLayerStore.internationalRegulatoryBundlesStore
  }

  get internationalRegulation() {
    return this.internationalRegulatoryBundlesStore.regulationMobileType
  }

  get businessInfoStore() {
    return this._businessInfoStore
  }

  get businessAddressStore() {
    return this._businessAddressFormStore
  }

  get contactInfoStore() {
    return this._contactInfoStore
  }

  get stepsStore() {
    return this._stepsStore
  }

  get isTermsAndPrivacyChecked() {
    return this._isTermsAndPrivacyChecked
  }

  get errorMessage() {
    return this.internationalRegulation?.registrationErrors
  }

  get errorStore() {
    return this._complianceLayerStore.errorStore
  }

  get organizationBusinessProfile() {
    return this._complianceLayerStore.organizationBusinessProfile
  }

  get allCountries() {
    return this._allCountries
  }

  get einItem() {
    return this._einItem
  }

  get nextDisabled() {
    if (this.stepsStore.step === 1 && this.businessInfoStore?.isNextDisabled) {
      return true
    }

    if (this.stepsStore.step === 2 && this._businessAddressFormStore?.isNextDisabled) {
      return true
    }

    if (this.stepsStore.step === 3 && this.contactInfoStore?.isNextDisabled) {
      return true
    }

    return this.stepsStore.step === 4
  }

  init = () => {
    this.loadCountries()
    this.loadRegulations()
    this.setInitialState()
  }

  setInitialState = () => {
    const isEmptyData = !this.internationalRegulation

    if (
      (this.organizationBusinessProfile?.businessInformation?.isUsaLocation ||
        this.organizationBusinessProfile?.businessInformation?.isCanadaLocation) &&
      isEmptyData
    ) {
      const { businessInformation, pointsOfContact } = this.organizationBusinessProfile

      const country = (businessInformation.country as ICountriesEnum) || null

      this.setEinItem({
        business_name: businessInformation.business_name,
        ein: businessInformation.registration_number,
        website_url: businessInformation.website_url,
        social_media_url: businessInformation.social_media_url,
        city: businessInformation.city,
        country: country,
        state_id: businessInformation.state_id,
        street_address_1: businessInformation.street_address_1,
        street_address_2: businessInformation.street_address_2,
        zip_code: businessInformation.zip_code,
      })

      if (pointsOfContact && pointsOfContact.firstContact) {
        this._contactInfoStore.setContactInfo(pointsOfContact.firstContact)
      }
    } else {
      if (this.internationalRegulation) {
        const key = this.internationalRegulation.businessData?.registrationAuthority
        if (key) {
          this.businessInfoStore.registrationAuthorityStore.setCountryByKey(key)
        }

        this.setEinItem({
          ...(this.internationalRegulation.businessData && {
            business_name: this.internationalRegulation.businessData.legalBusinessName,
            ein: this.internationalRegulation.businessData.businessRegistrationNumber,
            website_url: this.internationalRegulation.businessData.businessWebsiteUrl,
          }),
          ...(this.internationalRegulation.addressData && {
            city: this.internationalRegulation.addressData.city,
            country: this.internationalRegulation.addressData.country,
            state_id: this.internationalRegulation.addressData.state,
            street_address_1: this.internationalRegulation.addressData.businessAddress,
            street_address_2: this.internationalRegulation.addressData.businessAddress2,
            zip_code: this.internationalRegulation.addressData.zipCode,
          }),
        })

        if (this.internationalRegulation.authorizedRepresentativeData) {
          const { businessEmail, firstName, lastName, phoneNumber } =
            this.internationalRegulation.authorizedRepresentativeData

          this._contactInfoStore.setFirstName(firstName)
          this._contactInfoStore.setLastName(lastName)
          this._contactInfoStore.phoneInputStore.setNumber(phoneNumber)
          this._contactInfoStore.setEmail(businessEmail)
        }

        this._bundleId = this.internationalRegulation.id
      } else {
        // Initialize with empty einItem if no existing data
        this.setEinItem({})
      }
    }

    this._businessInfoStore.init()
    this._businessAddressFormStore.init().then(() => this.seedBusinessAddressForm())
  }

  private static mapEinItemToAddressData(
    einItem: IInternationalBusinessInformation | null
  ): IResponseEinItem | null {
    if (!einItem) return null
    return {
      street1: einItem.street_address_1 ?? '',
      street2: einItem.street_address_2 ?? '',
      city: einItem.city ?? '',
      state: einItem.state_id ?? '',
      zip: einItem.zip_code ?? '',
      ein: '',
      ein_formatted: '',
      name: '',
    }
  }

  private seedBusinessAddressForm = () => {
    const data = InternationalStepsStore.mapEinItemToAddressData(this._einItem)
    this._businessAddressFormStore.setAddressData(data)
    this._businessAddressFormStore.setActiveCountry(this._einItem?.country ?? '')
  }

  private syncBusinessAddressFieldToEinItem = (field: keyof IAddressData, value: string) => {
    const einItem = this._einItem ?? {}
    const mapping: Partial<Record<keyof IAddressData, keyof IInternationalBusinessInformation>> = {
      street1: 'street_address_1',
      street2: 'street_address_2',
      city: 'city',
      state: 'state_id',
      zip: 'zip_code',
      country: 'country',
    }
    const key = mapping[field]
    if (key) this.updateEinItem({ ...einItem, [key]: value })
  }

  dispose = () => {
    this._disposePageReaction?.()
    this._cancelPageSource?.cancel()
  }

  setEinItem = (value: IInternationalBusinessInformation | null) => {
    this._einItem = value
  }

  getProgressInfoTitle = (pathname: InternationalRoutes) => {
    return titleMap[pathname] || ''
  }

  getLayoutTitle = () => {
    return 'UK: Regulatory bundle'
  }

  loadCountries = async () => {
    if (this.allCountries.length) return

    try {
      const data = await AuthApi.getCountries()
      this._allCountries = data.data
    } catch (e) {
      logger.error(e)
    }
  }

  get regulationId() {
    return this._regulations?.find(
      (item) => item.country === this.internationalRegulatoryBundlesStore.regulatoryBundleCountry
    )?.id
  }

  loadRegulations = async () => {
    try {
      const { data } = await ComplianceApi.getAllInternationalComplianceRegulations()

      if ('data' in data && data.data) {
        this._regulations = data.data
      }
    } catch (e) {
      logger.error(e)
    }
  }

  initializeStepsForRoute = (route: InternationalRoutes) => {
    this.stepsStore.setTotalSteps(4)

    switch (route) {
      case InternationalRoutes.businessInfo:
        this.stepsStore.setStep(1)
        break
      case InternationalRoutes.businessAddress:
        this.stepsStore.setStep(2)
        break
      case InternationalRoutes.contactInfo:
        this.stepsStore.setStep(3)
        break
      case InternationalRoutes.finalDetails:
        this.stepsStore.setStep(4)
        break
      default:
        this.stepsStore.setStep(1)
    }
  }

  toggleIsTermsAndPrivacyChecked = () => {
    this._isTermsAndPrivacyChecked = !this._isTermsAndPrivacyChecked
  }

  closeSetting = () => {
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.international.root}`,
    })
  }

  decreaseStep = (path: InternationalRoutes) => {
    if (this.stepsStore.step > 1) {
      this.stepsStore.setStep(this.stepsStore.step - 1)

      let newPath

      if (path === InternationalRoutes.finalDetails) {
        newPath = InternationalRoutes.contactInfo
      }
      if (path === InternationalRoutes.contactInfo) {
        newPath = InternationalRoutes.businessAddress
      }
      if (path === InternationalRoutes.businessAddress) {
        newPath = InternationalRoutes.businessInfo
      }

      if (newPath) {
        uiStore.changeRoute({
          path: `/${SettingsPath.compliance.international.root}/${newPath}`,
        })
      }
    }
  }

  moveToSecondStep = async (path: InternationalRoutes) => {
    if (this.stepsStore.step < this.stepsStore.totalSteps) {
      let newPath

      if (path === InternationalRoutes.businessInfo) {
        const isNext = await this.submitBusinessInfo()

        if (!isNext) return

        newPath = InternationalRoutes.businessAddress
      }

      if (path === InternationalRoutes.businessAddress) {
        const isNext = await this.submitBusinessAddress()

        if (!isNext) return
        newPath = InternationalRoutes.contactInfo
      }

      if (path === InternationalRoutes.contactInfo) {
        const isNext = await this.submitAuthorizedRepresentative()

        if (!isNext) return
        newPath = InternationalRoutes.finalDetails
      }

      this.stepsStore.setStep(this.stepsStore.step + 1)

      if (newPath) {
        uiStore.changeRoute({
          path: `/${SettingsPath.compliance.international.root}/${newPath}`,
        })
      }
    }
  }

  updateEinItem = (einItem: IInternationalBusinessInformation) => {
    this._einItem = { ...this._einItem, ...einItem }
  }

  get fieldsError() {
    return this.errorStore.fieldsError
  }

  deleteErrorValue = (key: string) => {
    this.errorStore.deleteErrorValue(key)
  }

  submitBusinessInfo = async () => {
    if (!this.regulationId || this.businessInfoStore.isNextDisabled) {
      return false
    }

    try {
      this.stepsStore.setStepLoading(true)

      const { data } = await ComplianceApi.submitBusinessDataInternationalRegulatory({
        regulationId: this.regulationId,
        legalBusinessName: this.businessInfoStore.businessName ?? '',
        registrationAuthority: this.businessInfoStore.selectedCountryKey ?? '',
        businessRegistrationNumber: this.businessInfoStore.einFormatted ?? '',
        businessWebsiteUrl: this.businessInfoStore.hasWebsiteUrl
          ? this.businessInfoStore.websiteUrl ?? ''
          : this.businessInfoStore.socialMediaUrl ?? '',
        hasBusinessWebsite: this.businessInfoStore.hasWebsiteUrl,
        bundleId: this._bundleId,
      })

      if (data.data.id) this._bundleId = data.data.id

      return true
    } catch (e) {
      logger.error(e)
    } finally {
      this.stepsStore.setStepLoading(false)
    }
  }

  submitBusinessAddress = async () => {
    const store = this._businessAddressFormStore
    if (!this._bundleId || store.isNextDisabled) {
      return false
    }

    const ad = store.addressData
    try {
      this.stepsStore.setStepLoading(true)

      await ComplianceApi.submitAddressDataInternationalRegulatory({
        bundleId: this._bundleId,
        country: store.activeCountryId ?? '',
        businessAddress: ad?.street1 ?? '',
        businessAddress2: ad?.street2 ?? '',
        city: ad?.city ?? '',
        state: ad?.state ?? '',
        zipCode: ad?.zip ?? '',
      })

      return true
    } catch (e) {
      if (e instanceof AxiosError && e.errors.getError('zipCode')) {
        store.setZipLocalErrorMessage(e.errors.getError('zipCode'))
      }
      logger.error(e)
    } finally {
      this.stepsStore.setStepLoading(false)
    }
  }

  submitAuthorizedRepresentative = async () => {
    if (!this._bundleId || !this.contactInfoStore.isContactFilled) {
      return false
    }

    try {
      this.stepsStore.setStepLoading(true)

      await ComplianceApi.submitAuthorizedRepresentativeDataInternationalRegulatory({
        bundleId: this._bundleId,
        firstName: this.contactInfoStore.contact.firstName,
        lastName: this.contactInfoStore.contact.lastName,
        businessEmail: this.contactInfoStore.contact.email,
        phoneNumber: this.contactInfoStore.contact.phoneNumber,
      })

      return true
    } catch (e) {
      logger.error(e)
    } finally {
      this.stepsStore.setStepLoading(false)
    }
  }

  submitForReview = async () => {
    if (!this._bundleId) {
      return false
    }

    try {
      this.stepsStore.setStepLoading(true)

      await ComplianceApi.submitTermsInternationalRegulatory({
        bundleId: this._bundleId,
      })

      showToast({
        type: 'info',
        title: 'Regulatory bundle submitted for review',
      })

      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.international.root}`,
      })
    } catch (e) {
      logger.error(e)
    } finally {
      this.stepsStore.setStepLoading(false)
    }
  }
}
